import { imagePostUrl, POST_CARD_IMG_WIDTH } from 'lib/utils/imageUtils';
import { PostCaption } from 'components/PostCaption';
import { NETWORK_IDS, POST_TYPES } from 'lib/constants';
import {
  createAltTextForPosts, userIsAdmin,
} from 'lib/helpers';
import {
  useState, useEffect, useRef, MouseEvent, UIEvent,
} from 'react';
import { CardMedia } from '@mui/material';
import {
  PlayCircleFilledTwoTone as PlayButton, RemoveCircleOutline, KeyboardArrowLeft, KeyboardArrowRight,
} from '@mui/icons-material';
import { Post } from 'lib/types';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

interface RenderProps {
  post: Post;
  className?: string;
  fixedSize?: { width: number; height: number };
  greyOutCard?: boolean;
  hideOverlay?: boolean;
  newPostCard?: boolean;
}

export const PostCardMedia = ({
  post, className, fixedSize, greyOutCard, hideOverlay = false, newPostCard = false,
}: RenderProps) => {
  const [shouldRenderVideo, setShouldRenderVideo] = useState(!!post.videoUrl && !fixedSize);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const imgBgUrl = imagePostUrl(post.thumbnailUrl, {
    width: fixedSize?.width || POST_CARD_IMG_WIDTH,
    height: fixedSize?.height,
    isDead: !post.isLive,
    greyOut: greyOutCard,
    isLinkedinGif: post.network === NETWORK_IDS.linkedin && post.type === POST_TYPES.GIF,
    network: post.network,
  });
  const currentUser = useCurrentUser();
  const video = useRef<any>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /**
   * When we are loading the post, we set the min height
   * based on the network to reserve space for the thumbnail.
  */
  const getMinHeight = () => {
    if (fixedSize?.height) return fixedSize.height;
    switch (post.network) {
    case NETWORK_IDS.tiktok:
      return 400;
    case NETWORK_IDS.youtube:
      return 120;
    default:
      return 250;
    }
  };

  useEffect(() => {
    const shouldShowVideo = userIsAdmin(currentUser) && !!post.videoUrl;
    if (!shouldShowVideo) {
      setShouldRenderVideo(false);
    }
  }, [currentUser]);

  const pauseVideo = (e: MouseEvent<HTMLElement>) => {
    setIsVideoPlaying(false);
    video?.current?.pause();
    e.preventDefault();
  };

  const playVideo = (e: MouseEvent<HTMLElement>) => {
    setIsVideoPlaying(true);
    video?.current?.play();
    e.preventDefault();
  };

  const showOverlay = () => {
    if (hideOverlay) {
      return null;
    }
    if (!post.isLive) {
      return <RemoveCircleOutline fontSize="large" className="absolute bottom-1 left-1 text-white z-50" />;
    }
    if (shouldRenderVideo) {
      return (
        <PlayButton
          onClick={(e: any) => {
            if (!isVideoPlaying) {
              playVideo(e);
            } else {
              pauseVideo(e);
            }
          }}
          sx={{ fontSize: '100px' }}
          className={`transition-opacity duration-300 absolute left-0 right-0 top-0 bottom-0 m-auto z-0 text-white ${isVideoPlaying ? 'opacity-0' : 'opacity-100'}`}
        />
      );
    }
    if ((post.type === POST_TYPES.VIDEO || post.type === POST_TYPES.GIF) && !shouldRenderVideo) {
      return <PlayButton fontSize="large" className="absolute bottom-1 left-1 text-white z-50" />;
    }
    return null;
  };

  // This is all for carousel posts
  const [scrollLeft, setScrollLeft] = useState(0);
  const carousel = useRef<HTMLDivElement>(null);
  const carouselMediaWidth = fixedSize?.width || POST_CARD_IMG_WIDTH;

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const scroll = e.currentTarget.scrollLeft;
    setScrollLeft(scroll);
  };

  const goLeft = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!carousel?.current) return;

    const firstChild = carousel.current.firstElementChild as HTMLElement;
    const itemWidth = firstChild?.getBoundingClientRect().width || carouselMediaWidth;

    if (scrollLeft <= 0) return;
    carousel.current.scrollLeft = scrollLeft - itemWidth;
  };

  const goRight = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!carousel?.current) return;

    const firstChild = carousel.current.firstElementChild as HTMLElement;
    const itemWidth = firstChild?.getBoundingClientRect().width || carouselMediaWidth;

    carousel.current.scrollLeft = scrollLeft + itemWidth;
  };

  const disableLeft = () => {
    if (!carousel?.current) return true;

    const firstChild = carousel.current.firstElementChild as HTMLElement;
    const itemWidth = firstChild?.getBoundingClientRect().width || carouselMediaWidth;

    // Disable if the scroll position is at the start
    if (isMobile) {
      return carousel.current.scrollLeft <= 0;
    }
    return carousel.current.scrollLeft - itemWidth <= 0;
  };

  const disableRight = () => {
    if (!carousel?.current) return true;

    const sw = carousel.current.scrollWidth; // Total scrollable width
    const ow = carousel.current.offsetWidth; // Visible width of the carousel
    const maxScrollLeft = sw - ow; // Maximum scrollable position

    // Disable if the scroll position is at or beyond the maximum scrollable position
    return carousel.current.scrollLeft >= maxScrollLeft;
  };

  const carouselImage = (url: string) => imagePostUrl(url, {
    width: fixedSize?.width || carouselMediaWidth,
    height: fixedSize?.height || carouselMediaWidth,
    isDead: !post.isLive,
    greyOut: greyOutCard,
  });

  if ((post.carouselMedia?.length || 0) > 1 && userIsAdmin(currentUser)) {
    return (
      <div className={className} key={`post-card-media-${post.id}-carousel`}>
        <button
          type="button"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white opacity-70 hover:opacity-100 disabled:opacity-30 rounded-full w-7 h-7 flex items-center justify-center"
          onClick={goLeft}
          disabled={disableLeft()}
          aria-label="previous"
        >
          <KeyboardArrowLeft fontSize="small" color="inherit" />
        </button>
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white opacity-70 hover:opacity-100 disabled:opacity-30 rounded-full w-7 h-7 flex items-center justify-center"
          onClick={goRight}
          disabled={disableRight()}
          aria-label="next"
        >
          <KeyboardArrowRight fontSize="small" />
        </button>
        <div
          ref={carousel}
          onScroll={onScroll}
          className={`relative flex overflow-x-scroll scroll-smooth snap-x z-1 items-center ${newPostCard && 'rounded-md'}`}
        >
          {post.carouselMedia?.map((media) => (
            <>
              <CardMedia
                key={`post-card-media-${media}-${post.id}`}
                component={shouldRenderVideo ? 'video' : 'img'}
                alt={createAltTextForPosts(post)}
                src={carouselImage(media)}
                poster={carouselImage(media)}
                suppressHydrationWarning
              />
              {showOverlay()}
            </>
          ))}
        </div>
      </div>
    );
  }

  if (imgBgUrl) {
    return (
      <div className={className}>
        <CardMedia
          key={`post-card-media-${post.id}`}
          ref={video}
          component={shouldRenderVideo ? 'video' : 'img'}
          loading="lazy"
          alt={createAltTextForPosts(post)}
          image={shouldRenderVideo ? post.videoUrl : undefined}
          controlsList="noDownload"
          src={shouldRenderVideo ? post.videoUrl : imgBgUrl}
          poster={imgBgUrl}
          onError={() => setShouldRenderVideo(false)}
          loop
          playsInline
          muted
          onClick={(e: MouseEvent<HTMLElement>) => {
            if (shouldRenderVideo && isVideoPlaying) {
              pauseVideo(e);
            }
          }}
          suppressHydrationWarning
          sx={{
            borderRadius: newPostCard ? '5px' : '0px',
            minHeight: `${getMinHeight()}px`,
            minWidth: '220px',
          }}
        />
        {showOverlay()}
      </div>
    );
  }

  return (
    <PostCaption networkId={post.network} className="bg-[#2c2c2c] text-white p-4 whitespace-pre-wrap mt-4">
      {post.caption?.slice(0, 300)}
    </PostCaption>
  );
};
